import { dispatchCustomEvent } from './ui-helpers';
import type { UIElement } from '../components/ui-element';

/**
 * Axis the current gesture has been locked to once it passes the axis-lock
 * threshold. 'none' means that the direction has not been decided yet.
 */
type SwipeAxisLock = 'none' | 'horizontal' | 'vertical';

/**
 * @alias SwipeObserverOptions
 */
export interface SwipeObserverOptions {
    /**
     * If true then the observer decides the dominant axis of the gesture and
     * calls preventDefault() on horizontal moves so the page cannot scroll
     * vertically while the user swipes a horizontal carousel.
     * Vertical gestures are left to the browser so native page scrolling still works.
     * Optional to avoid affecting other consumers (like ui-modal).
     */
    lockHorizontalAxis?: boolean;
}

/**
 * @alias SwipeObserver
 * @classdesc Class for handling swipe gestures.
 * @example
 * const observer = new SwipeObserver(document.body);
 * document.body.addEventListener('swipe-left', (e) => console.log('Swiped left!', e));
 * document.body.addEventListener('swipe-right', (e) => console.log('Swiped right!', e));
 * document.body.addEventListener('swipe-bottom', (e) => console.log('Swiped bottom!', e));
 * document.body.addEventListener('swipe-top', (e) => console.log('Swiped top!', e));
 * observer.disconnect();
 */
export class SwipeObserver {
    element: HTMLElement | UIElement;
    threshold: number;
    swipeStartTime: number;
    swipeStartX: number;
    swipeStartY: number;
    private axisLock: SwipeAxisLock;
    private readonly lockHorizontalAxis: boolean;
    private readonly swipeStartHandler: EventListener;
    private readonly swipeMoveHandler: EventListener;
    private readonly swipeEndHandler: EventListener;

    /**
     * Pixels per millisecond.
     */
    static readonly SwipeSpeedThreshold = 0.8;

    /**
     * Pixels.
     */
    static readonly SwipeDistanceThreshold = 100;

    /**
     * Movement in pixels before an axis is decided when lockHorizontalAxis is enabled.
     */
    static readonly AxisLockThreshold = 8;

    /**
     * @param {HTMLElement} element
     * @param {number} [threshold] Min swipe distance in px required to commit the gesture.
     * A shorter/ slower swipe still commits when it exceeds SwipeSpeedThreshold.
     * Defaults to SwipeDistanceThreshold.
     * @param {SwipeObserverOptions} [options]
     * @property {HTMLElement} element Element to observe
     * @property {number} threshold Threshold in pixels
     */
    constructor(
        element: HTMLElement,
        threshold: number = SwipeObserver.SwipeDistanceThreshold,
        options: SwipeObserverOptions = {}
    ) {
        if (!(element instanceof HTMLElement)) {
            throw new TypeError('Element has to be instance of HTMLElement');
        }
        this.element = element;
        this.threshold = threshold;
        this.lockHorizontalAxis = options.lockHorizontalAxis ?? false;
        this.axisLock = 'none';

        this.swipeStartTime = -1;
        this.swipeStartX = 0;
        this.swipeStartY = 0;

        this.swipeStartHandler = this.handleSwipeStart.bind(
            this
        ) as EventListener;
        this.swipeMoveHandler = this.handleSwipeMove.bind(
            this
        ) as EventListener;
        this.swipeEndHandler = this.handleSwipeEnd.bind(this) as EventListener;
        this.subscribeSwipeEvents();
    }

    /**
     * Destroy all listeners
     */
    disconnect() {
        this.unsubscribeSwipeEvents();
    }

    /**
     * @private
     */
    subscribeSwipeEvents() {
        this.element.addEventListener('touchstart', this.swipeStartHandler, {
            capture: false,
            passive: true,
        });
        this.element.addEventListener('touchend', this.swipeEndHandler, false);
        this.element.addEventListener(
            'pointerdown',
            this.swipeStartHandler,
            false
        );
        this.element.addEventListener('pointerup', this.swipeEndHandler, false);

        if (this.lockHorizontalAxis) {
            this.element.addEventListener('touchmove', this.swipeMoveHandler, {
                passive: false,
            });
        }
    }

    /**
     * @private
     */
    unsubscribeSwipeEvents() {
        this.element.removeEventListener(
            'touchstart',
            this.swipeStartHandler,
            false
        );
        this.element.removeEventListener(
            'touchend',
            this.swipeEndHandler,
            false
        );
        this.element.removeEventListener(
            'pointerdown',
            this.swipeStartHandler,
            false
        );
        this.element.removeEventListener(
            'pointerup',
            this.swipeEndHandler,
            false
        );

        if (this.lockHorizontalAxis) {
            this.element.removeEventListener(
                'touchmove',
                this.swipeMoveHandler
            );
        }
    }

    /**
     * Touch devices fire compatibility pointer events (pointerType 'touch')
     * together with the native touch events so a single gesture would otherwise be
     * handled twice. Touch input is handled through the touch events, leaving
     * pointer events to cover mouse and pen only.
     * @param {PointerEvent | TouchEvent} event
     * @private
     */
    isRedundantTouchPointerEvent(event: PointerEvent | TouchEvent): boolean {
        return event instanceof PointerEvent && event.pointerType === 'touch';
    }

    /**
     * @param {PointerEvent | TouchEvent} event
     * @private
     */
    handleSwipeStart(event: PointerEvent | TouchEvent) {
        if (this.isRedundantTouchPointerEvent(event)) {
            return;
        }
        this.swipeStartX = event.type.includes('touch')
            ? (event as TouchEvent).touches[0].clientX
            : (event as PointerEvent).clientX;
        this.swipeStartY = event.type.includes('touch')
            ? (event as TouchEvent).touches[0].clientY
            : (event as PointerEvent).clientY;
        this.swipeStartTime = Date.now();
        this.axisLock = 'none';
    }

    /**
     * Locks the gesture to its dominant axis.
     * For horizontal gestures it prevents the browser from scrolling the page.
     * Only attached when lockHorizontalAxis is enabled.
     * @param {TouchEvent} event
     * @private
     */
    handleSwipeMove(event: TouchEvent) {
        if (event.touches.length !== 1) {
            return;
        }

        const deltaX = Math.abs(event.touches[0].clientX - this.swipeStartX);
        const deltaY = Math.abs(event.touches[0].clientY - this.swipeStartY);

        if (this.axisLock === 'none') {
            if (Math.max(deltaX, deltaY) < SwipeObserver.AxisLockThreshold) {
                return;
            }
            this.axisLock = deltaX > deltaY ? 'horizontal' : 'vertical';
        }

        if (this.axisLock === 'horizontal') {
            event.preventDefault();
        }
    }

    /**
     * @param {PointerEvent | TouchEvent} event
     * @private
     */
    handleSwipeEnd(event: PointerEvent | TouchEvent) {
        if (this.isRedundantTouchPointerEvent(event)) {
            return;
        }
        const swipeEndX = event.type.includes('touch')
            ? (event as TouchEvent).changedTouches[0].clientX
            : (event as PointerEvent).clientX;
        const swipeEndY = event.type.includes('touch')
            ? (event as TouchEvent).changedTouches[0].clientY
            : (event as PointerEvent).clientY;
        const swipeXDelta = swipeEndX - this.swipeStartX;
        const swipeYDelta = swipeEndY - this.swipeStartY;
        const absoluteSwipeXDelta = Math.abs(swipeXDelta);
        const absoluteSwipeYDelta = Math.abs(swipeYDelta);

        const primaryDistance = Math.max(
            absoluteSwipeXDelta,
            absoluteSwipeYDelta
        );
        const timeMs = Math.max(Date.now() - this.swipeStartTime, 1);
        const speed = primaryDistance / timeMs; // px per ms

        const details = {
            swipeStartX: this.swipeStartX,
            swipeStartY: this.swipeStartY,
            swipeEndX,
            swipeEndY,
            swipeXDelta,
            swipeYDelta,
            originalEvent: event,
        };

        if (
            speed < SwipeObserver.SwipeSpeedThreshold &&
            primaryDistance < this.threshold
        ) {
            return;
        }

        const direction =
            absoluteSwipeXDelta > absoluteSwipeYDelta
                ? swipeXDelta > 0
                    ? 'right'
                    : 'left'
                : swipeYDelta > 0
                  ? 'bottom'
                  : 'top';

        dispatchCustomEvent(this.element, `swipe-${direction}`, details);
    }
}
